import { useState } from 'react';
import type { SettlementView } from '@cuito/types';
import { Button } from '@cuito/ui';
import { beijingDayRange } from '../../api/ledger';
import { settlementsApi, type SettleSettlementResult } from '../../api/settlements';
import { LOAD_FAILED_TEXT, settlementErrorText } from './settlementLabels';

/**
 * 标记已结算（UI 规范 554 行：日期 + 凭证 + 备注）。
 *
 * ## `settle(id, body)` 是**两参**，没有 `Idempotency-Key`（A-P7-46）
 *
 * 契约 §15.4 与冻结文档 §五都是 `settle(id, body)`：标记已结算是**单向状态跃迁**
 * （`PENDING → SETTLED`），重复调用由服务端的 `409 SETTLEMENT_ALREADY_SETTLED` 表达
 * （逐字文案 `结算单已结算，不可重复操作`），不靠幂等键重放。
 * ⇒ 本弹窗**不持键**、不生成 uuid：那把键属于「生成结算单」（范围键幂等），不属于这里。
 *
 * ## 日期 → ISO 复用 `beijingDayRange`（不写第二份北京日换算）
 *
 * `<input type="date">` 给的是北京日 `YYYY-MM-DD`，而契约 §15.4 要 `@IsISO8601()`。
 * `api/ledger.ts` 的 `beijingDayRange()` 已经是本仓唯一的「北京日 → ISO 闭区间」实现
 * （它有回环校验与 TZ 无关的用例）⇒ 这里取它的 `from`（北京当日 00:00），不另写一份。
 */
interface Props {
  settlement: SettlementView;
  onClose: () => void;
  onSettled: (result: SettleSettlementResult) => void;
}

export default function SettleDialog({ settlement, onClose, onSettled }: Props) {
  const [day, setDay] = useState('');
  const [voucherUrl, setVoucherUrl] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const range = beijingDayRange(day);
  const canSubmit = range !== null && !submitting;

  const submit = async (): Promise<void> => {
    if (range === null || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await settlementsApi.settle(settlement.id, {
        settlementDate: range.from,
        ...(voucherUrl.trim() === '' ? {} : { voucherUrl: voucherUrl.trim() }),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      });
      onSettled(result);
    } catch (e) {
      setError(settlementErrorText(e, LOAD_FAILED_TEXT));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        role="dialog"
        data-testid="settlement-settle-dialog"
        className="w-[min(560px,92vw)] rounded-lg border-2 border-black bg-white p-5"
      >
        <h2 className="mb-1 text-xl font-bold">标记已结算</h2>
        <p className="mb-4 text-sm text-gray-500">
          {settlement.code} · 应付净额 {settlement.netSettlement}
        </p>

        <label className="mb-3 flex flex-col gap-1">
          <span className="text-sm font-bold">结算日期</span>
          <input
            type="date"
            data-testid="settlement-settle-date"
            value={day}
            onChange={(event) => setDay(event.target.value)}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
          />
        </label>

        <label className="mb-3 flex flex-col gap-1">
          <span className="text-sm font-bold">凭证</span>
          <input
            type="text"
            data-testid="settlement-settle-voucher"
            value={voucherUrl}
            maxLength={500}
            placeholder="/api/files/general/voucher.png"
            onChange={(event) => setVoucherUrl(event.target.value)}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
          />
        </label>

        <label className="mb-3 flex flex-col gap-1">
          <span className="text-sm font-bold">备注</span>
          <input
            type="text"
            data-testid="settlement-settle-note"
            value={note}
            maxLength={200}
            onChange={(event) => setNote(event.target.value)}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
          />
        </label>

        {error !== null && (
          <p
            data-testid="settlement-settle-error"
            className="mb-3 rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600"
          >
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button data-testid="settlement-settle-submit" onClick={() => void submit()} disabled={!canSubmit}>
            标记已结算
          </Button>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
        </div>
      </div>
    </div>
  );
}
